import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigation } from '@react-navigation/native';

import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { triggerHapticFeedback } from '@/utils/haptic-feedback';

import { AddSelectionConflictingError, AddSelectionMaxSelectionsError, SelectionParam } from '../types';
import { useSbkBetSlipStore } from './use-sbk-betslip-store';

export const useIsOptionSelected = (optionId: string) => useSbkBetSlipStore(state => !!state.options[optionId]);

export const useSelectionActions = () => {
    const addNewSelection = useSbkBetSlipStore(state => state.actions.addSelection);
    const removeSelection = useSbkBetSlipStore(state => state.actions.removeSelection);
    const toggleComboSelectionStatus = useSbkBetSlipStore(state => state.actions.toggleComboSelectionStatus);
    const { showToast, showInfoSheet } = useAlerts();
    const { t } = useTranslation('betslip_sbk');
    const isPredictions = useIsPredictionsProduct();
    const navigation = useNavigation();

    const addSelection = async ({ option, market, event }: SelectionParam) => {
        triggerHapticFeedback();
        try {
            await addNewSelection(option, market, event);
            // Predictions entry slip opens as soon as a selection is added
            if (isPredictions) {
                navigation.navigate('BetSlipStack', { screen: 'BetSlipModal', params: { screen: 'Singles' } });
            }
        } catch (e: unknown) {
            if (e instanceof AddSelectionConflictingError) {
                const selectionId = option.id;
                showInfoSheet({
                    title: t('conflicting_selections_title'),
                    description: t('conflicting_selections_description'),
                    buttonLabel: t('conflicting_selections_primary_button'),
                    secondaryLabel: t('conflicting_selections_secondary_button'),
                    handlePress: () => {
                        toggleComboSelectionStatus(selectionId);
                    },
                    handleSecondaryPress: () => {
                        removeSelection(selectionId);
                    },
                });
            } else if (e instanceof AddSelectionMaxSelectionsError) {
                showToast({ message: 'Your entry slip is maxed out', toastType: 'warning' });
            }
        }
    };

    const removeSelectionWithHaptic = useCallback(
        (optionId: string) => {
            triggerHapticFeedback();
            removeSelection(optionId);
        },
        [removeSelection]
    );

    return { addSelection, removeSelection: removeSelectionWithHaptic };
};
